import { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { Copy, Download, QrCode, Share2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/toast';

function safeFilename(value) {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'ride-pass';
}

function loadImage(source) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = source;
  });
}

export function RidePassQr({ value, title = 'Convoy Ride Pass', code = '', compact = false }) {
  const [dataUrl, setDataUrl] = useState('');
  const toast = useToast();

  useEffect(() => {
    let active = true;
    if (!value) {
      setDataUrl('');
      return undefined;
    }
    QRCode.toDataURL(value, {
      width: compact ? 220 : 360,
      margin: 1,
      errorCorrectionLevel: 'H',
      color: { dark: '#061019', light: '#F5F8FC' },
    }).then((result) => {
      if (active) setDataUrl(result);
    }).catch(() => {
      if (active) setDataUrl('');
    });
    return () => { active = false; };
  }, [compact, value]);

  const copy = async () => {
    await navigator.clipboard.writeText(value);
    toast.show('Ride Pass link copied', 'success');
  };

  const share = async () => {
    if (!navigator.share) {
      await copy();
      return;
    }
    await navigator.share({
      title,
      text: code ? `${title}\nRide Pass: ${code}` : title,
      url: value,
    });
  };

  const downloadPoster = async () => {
    if (!dataUrl) return;
    const image = await loadImage(dataUrl);
    const canvas = document.createElement('canvas');
    canvas.width = 900;
    canvas.height = 1200;
    const context = canvas.getContext('2d');
    const gradient = context.createLinearGradient(0, 0, 900, 1200);
    gradient.addColorStop(0, '#07131d');
    gradient.addColorStop(0.55, '#040810');
    gradient.addColorStop(1, '#020407');
    context.fillStyle = gradient;
    context.fillRect(0, 0, 900, 1200);
    context.strokeStyle = 'rgba(69,240,230,.35)';
    context.lineWidth = 3;
    context.strokeRect(44, 44, 812, 1112);
    context.fillStyle = '#45F0E6';
    context.font = '800 24px sans-serif';
    context.textAlign = 'center';
    context.fillText('CONVOY RIDE PASS', 450, 125);
    context.fillStyle = '#F5F8FC';
    context.font = '900 50px sans-serif';
    const headline = title.length > 30 ? `${title.slice(0, 29)}…` : title;
    context.fillText(headline, 450, 205);
    context.fillStyle = '#94A3B8';
    context.font = '600 22px sans-serif';
    context.fillText('Scan to join the club or convoy', 450, 252);
    context.fillStyle = '#F5F8FC';
    context.fillRect(205, 315, 490, 490);
    context.drawImage(image, 225, 335, 450, 450);
    if (code) {
      context.fillStyle = '#45F0E6';
      context.font = '800 20px monospace';
      context.fillText('MANUAL CODE', 450, 875);
      context.fillStyle = '#F5F8FC';
      context.font = '900 44px monospace';
      context.fillText(code, 450, 930);
    }
    context.fillStyle = '#64748B';
    context.font = '600 18px sans-serif';
    context.fillText('Convoy · Ride together. Never lose a rider.', 450, 1085);
    const link = document.createElement('a');
    link.href = canvas.toDataURL('image/png');
    link.download = `${safeFilename(title)}-ride-pass.png`;
    link.click();
  };

  return <div className={`ride-pass-qr ${compact ? 'ride-pass-qr--compact' : ''}`}>
    <div className="ride-pass-qr__scanline"/>
    <div className="ride-pass-qr__corners" aria-hidden="true"/>
    {dataUrl ? <img src={dataUrl} alt={`${title} QR code`} className="ride-pass-qr__image"/> : <div className="grid aspect-square place-items-center text-cyan-300"><QrCode size={42}/></div>}
    <div className="mt-3 grid grid-cols-3 gap-2">
      <Button type="button" variant="secondary" onClick={() => void copy()} disabled={!value}><Copy size={14}/><span className="hidden 2xl:inline">Copy</span></Button>
      <Button type="button" variant="secondary" onClick={() => void share()} disabled={!value}><Share2 size={14}/><span className="hidden 2xl:inline">Share</span></Button>
      <Button type="button" variant="secondary" onClick={() => void downloadPoster()} disabled={!dataUrl}><Download size={14}/><span className="hidden 2xl:inline">Poster</span></Button>
    </div>
  </div>;
}
